import axios from "axios";
import { axiosInstance } from "../../utils/axios";
import env from "../../utils/env";
import { BACCHUS_API_URL } from "./constants";
import {
  Segment,
  SegmentWriteBody,
  SegmentUpdateBody,
  SegmentListResponse,
  SingleSegmentResponse,
  SegmentFilterBy,
} from "./types";
import { CreateShareLinkBody, ShareLink } from "./shareTypes";

export interface FetchSegmentsParams {
  page?: number;
  pageSize?: number;
  filterBy?: SegmentFilterBy;
  /** Search segments by name (case and accent insensitive) */
  search?: string;
  /** AbortController signal for request cancellation */
  signal?: AbortSignal;
}

/**
 * Fetch segments with pagination, filtering, and search
 */
export async function fetchSegments({
  page = 1,
  pageSize = 50,
  filterBy = "my",
  search,
  signal,
}: FetchSegmentsParams = {}): Promise<SegmentListResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
    filter_by: filterBy,
  });

  if (search) {
    params.set("search", search);
  }

  const { data } = await axiosInstance.get<SegmentListResponse>(
    `${BACCHUS_API_URL}/segments?${params.toString()}`,
    { signal }
  );

  return data;
}

/**
 * Get single segment by ID
 */
export async function getSegment(id: string): Promise<Segment> {
  const { data } = await axiosInstance.get<SingleSegmentResponse>(
    `${BACCHUS_API_URL}/segments/${id}`
  );

  return data.data!;
}

/**
 * Create new segment
 */
export async function createSegment(
  segmentData: SegmentWriteBody
): Promise<Segment> {
  const { data } = await axiosInstance.post<SingleSegmentResponse>(
    `${BACCHUS_API_URL}/segments`,
    segmentData
  );

  return data.data!;
}

/**
 * Update existing segment
 */
export async function updateSegment(
  id: string,
  segmentData: SegmentUpdateBody
): Promise<Segment> {
  const { data } = await axiosInstance.patch<SingleSegmentResponse>(
    `${BACCHUS_API_URL}/segments/${id}`,
    segmentData
  );

  // Its share link's snapshot was computed from the old filters.
  refreshShareSnapshot(id, { force: false }).catch(() => {});

  return data.data!;
}

/**
 * Delete segment by ID
 */
export async function deleteSegment(id: string): Promise<void> {
  await axiosInstance.delete(`${BACCHUS_API_URL}/segments/${id}`);
}

/**
 * Create the segment's public share link, or refresh the existing one in
 * place (same token). Only the segment's creator may call this.
 */
export const createShareLink = async (
  segmentId: string,
  body: CreateShareLinkBody
): Promise<ShareLink> => {
  const { data } = await axiosInstance.post<{ data: ShareLink }>(
    `${BACCHUS_API_URL}/segments/${segmentId}/share-links`,
    body
  );
  return data.data;
};

/** The segment's share link, or null when it was never shared. */
export const getShareLink = async (
  segmentId: string
): Promise<ShareLink | null> => {
  try {
    const { data } = await axiosInstance.get<{ data: ShareLink }>(
      `${BACCHUS_API_URL}/segments/${segmentId}/share-link`
    );
    return data.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};

/**
 * Asks piq to recompute the segment's share-link snapshot; piq skips it when
 * nothing it depends on changed, unless `force`. Without a snapshot the share
 * page answers live, so callers fire and forget. Plain fetch, not
 * axiosInstance: its 401 handler would log the user out over a background call.
 */
export async function refreshShareSnapshot(
  segmentId: string,
  { force }: { force: boolean }
): Promise<void> {
  const token = localStorage.getItem("access_token");
  await fetch(`${env.PLATFORM_URL_BASE}/share-api/market-sizing/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ segmentId, force }),
  });
}
